/**
 * Admin Messages - Supabase Direct
 */

import React, { useState, useEffect } from 'react';
import { Mail, MailOpen, Trash2, Phone, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { contactsAPI } from '../../utils/apiService';
import { supabase } from '../../supabaseClient';

export const AdminMessages = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState(null);

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async () => {
        try {
            // Ordenar por data (mais recente primeiro)
            const { data, error } = await supabase
                .from('contacts')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setMessages(data || []);
        } catch (error) {
            console.error('Error fetching messages:', error);
            setMessages([]);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkRead = async (id) => {
        try {
            // 1. Atualizar no Supabase e verificar erro
            const { error } = await supabase
                .from('contacts')
                .update({ is_read: true })
                .eq('id', id);

            if (error) throw error;
            
            // 2. Atualizar estado local da lista
            setMessages(prev => prev.map(m => 
                m.id === id ? { ...m, is_read: true } : m
            ));

            // 3. Atualizar mensagem selecionada se for a mesma
            if (selectedMessage?.id === id) {
                setSelectedMessage(prev => ({ ...prev, is_read: true }));
            }

        } catch (error) {
            console.error('Error marking read:', error);
            toast.error('Erro ao atualizar estado');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Eliminar esta mensagem?')) return;

        try {
            const { error } = await supabase
                .from('contacts')
                .delete()
                .eq('id', id);

            if (error) throw error;

            toast.success('Mensagem eliminada');
            setMessages(prev => prev.filter(m => m.id !== id));
            if (selectedMessage?.id === id) setSelectedMessage(null);
        } catch (error) {
            console.error('Erro delete:', error);
            toast.error('Erro ao eliminar');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('pt-PT', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const unreadCount = messages.filter(m => !m.is_read).length;

    return (
        <div className="space-y-6">
            <header>
                <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] dark:text-white">Mensagens</h1>
                <p className="text-[#666666] dark:text-gray-400 mt-1">
                    {unreadCount > 0 
                        ? `${unreadCount} mensagem${unreadCount > 1 ? 's' : ''} não lida${unreadCount > 1 ? 's' : ''}`
                        : 'Todas as mensagens lidas'
                    }
                </p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 bg-white dark:bg-[#1A1A1A] border border-[#E5E5E5] dark:border-[#333] rounded-[4px] overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-[#666666] dark:text-gray-400">A carregar...</div>
                    ) : messages.length === 0 ? (
                        <div className="p-8 text-center text-[#666666] dark:text-gray-400">
                            <Mail size={48} className="mx-auto mb-4 opacity-30" />
                            <p>Nenhuma mensagem</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-[#E5E5E5] dark:divide-[#333] max-h-[600px] overflow-y-auto">
                            {messages.map((message) => (
                                <button
                                    key={message.id}
                                    onClick={() => {
                                        setSelectedMessage(message);
                                        // Só marca como lida se ainda não estiver
                                        if (!message.is_read) handleMarkRead(message.id);
                                    }}
                                    className={`w-full text-left p-4 hover:bg-[#F9F9F9] dark:hover:bg-[#222] transition-colors ${
                                        selectedMessage?.id === message.id ? 'bg-[#F4F4F4] dark:bg-[#222]' : ''
                                    } ${!message.is_read ? 'bg-red-50 dark:bg-red-900/10' : ''}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                {!message.is_read && <span className="w-2 h-2 bg-[#E60000] rounded-full" />}
                                                <span className="font-semibold text-[#1A1A1A] dark:text-white truncate">{message.name}</span>
                                            </div>
                                            <p className="text-sm text-[#666666] dark:text-gray-400 truncate mt-1">{message.message}</p>
                                            <span className="text-xs text-[#999999] dark:text-gray-500 mt-1 block">{formatDate(message.created_at)}</span>
                                        </div>
                                        {message.is_read ? (
                                            <MailOpen size={16} className="text-[#999999]" />
                                        ) : (
                                            <Mail size={16} className="text-[#E60000]" />
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="lg:col-span-2 bg-white dark:bg-[#1A1A1A] border border-[#E5E5E5] dark:border-[#333] rounded-[4px] p-6">
                    {selectedMessage ? (
                        <div>
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <h2 className="font-bold text-xl text-[#1A1A1A] dark:text-white">{selectedMessage.name}</h2>
                                    <span className="text-sm text-[#999999] dark:text-gray-500">{formatDate(selectedMessage.created_at)}</span>
                                </div>
                                <button
                                    onClick={() => handleDelete(selectedMessage.id)}
                                    className="p-2 text-[#666666] hover:text-[#E60000] hover:bg-red-50 dark:hover:bg-red-900/20 rounded-[2px] transition-colors"
                                    title="Eliminar"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>

                            <div className="space-y-3 mb-6 p-4 bg-[#F4F4F4] dark:bg-[#222] rounded-[4px]">
                                <div className="flex items-center gap-2 text-sm">
                                    <Mail size={16} className="text-[#999999]" />
                                    <a href={`mailto:${selectedMessage.email}`} className="text-[#E60000] hover:underline">{selectedMessage.email}</a>
                                </div>
                                {selectedMessage.phone && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <Phone size={16} className="text-[#999999]" />
                                        <a href={`tel:${selectedMessage.phone}`} className="text-[#1A1A1A] dark:text-white hover:text-[#E60000]">{selectedMessage.phone}</a>
                                    </div>
                                )}
                            </div>

                            <div className="mb-6">
                                <h3 className="font-semibold text-sm text-[#1A1A1A] dark:text-white mb-2">Mensagem</h3>
                                <p className="text-[#666666] dark:text-gray-400 whitespace-pre-wrap leading-relaxed">{selectedMessage.message}</p>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <a 
                                    href={`mailto:${selectedMessage.email}`}
                                    className="flex items-center gap-2 px-4 py-2 bg-[#E60000] text-white rounded-[2px] font-semibold hover:bg-[#CC0000] transition-colors"
                                >
                                    <Mail size={16} />
                                    Responder por Email
                                </a>
                                {selectedMessage.phone && (
                                    <>
                                        <a 
                                            href={`tel:${selectedMessage.phone}`}
                                            className="flex items-center gap-2 px-4 py-2 border border-[#E5E5E5] dark:border-[#333] text-[#1A1A1A] dark:text-white rounded-[2px] font-semibold hover:border-[#1A1A1A] dark:hover:border-[#555] transition-colors"
                                        >
                                            <Phone size={16} />
                                            Ligar
                                        </a>
                                        <a 
                                            href={`https://wa.me/${selectedMessage.phone.replace(/[^0-9]/g, '')}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white rounded-[2px] font-semibold hover:bg-[#1DA851] transition-colors"
                                        >
                                            <ExternalLink size={16} />
                                            WhatsApp
                                        </a>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center text-[#999999] min-h-[400px]">
                            <div className="text-center">
                                <Mail size={48} className="mx-auto mb-4 opacity-30" />
                                <p>Selecione uma mensagem</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
